import { useEffect, useCallback, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { Flex, Typography, Form, Input, Switch, App, Button, Spin } from "antd";

import Breadcrumbs from "@/components/ui/Breadcrumbs";

import FiltersList from "./components/FiltersList";
import TextSectionsList from "./components/TextSectionsList";

import routes from "@/routes";

import { getItem, createItem, updateItem } from "@/api/reports";
import { getDefinitionOptions } from "@/api/definitions";

const INITIAL_DATA = {
  name: "",
  visibility: true,
  filters: [],
  text: [],
};

const ReportSettings = () => {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const { message } = App.useApp();

  const [pending, setPending] = useState(false);

  const [form] = Form.useForm();
  const [error, setError] = useState(null);

  const [definitionsOptions, setDefinitionsOptions] = useState([]);

  const breadcrumbItems = useMemo(
    () => [
      { title: "Административная панель" },
      {
        title: "Настройки системы",
        href: routes.administration.systemSettings,
      },
      { title: isNew ? "Создание отчета" : "Редактирование отчета" },
    ],
    [isNew]
  );

  const fetchData = useCallback(async () => {
    if (isNew) return;
    try {
      setPending(true);
      const data = await getItem(id);
      form.setFieldsValue(data);
      setPending(false);
    } catch (error) {
      message.open({
        type: "error",
        content: error?.message || String(error),
        duration: 10,
      });
    }
  }, [id, message, form, isNew]);

  const fetchDefinitionsOptions = async () => {
    try {
      const definitions = await getDefinitionOptions({ type: "object" });
      setDefinitionsOptions(definitions);
    } catch (error) {}
  };

  useEffect(() => {
    fetchDefinitionsOptions();
    // Загрузка данных отчета по id, если id присутствует
    if (!isNew) {
      fetchData();
    } else {
      form.setFieldsValue(INITIAL_DATA);
    }
  }, [isNew, fetchData, form]);

  const handleCancel = () => {
    navigate(routes.administration.systemSettings);
  };

  const onOk = () => {
    form
      .validateFields()
      .then((values) => {
        handleSave(values);
      })
      .catch((info) => {
        console.log("Ошибка валидации:", info);
      });
  };

  const handleSave = async (values) => {
    setPending(true);
    setError(null);

    const payload = {
      ...values,
      text: (values.text ?? []).map((section, i) => ({
        ...section,
        idx: i + 1,
      })),
    };

    try {
      if (isNew) {
        await createItem(payload);
      } else {
        await updateItem({ uuid: id, ...payload });
      }

      navigate(routes.administration.systemSettings);
    } catch (error) {
      setError(error.message);
    } finally {
      setPending(false);
    }
  };

  return (
    <Flex vertical gap={10}>
      <Breadcrumbs items={breadcrumbItems} />

      <Flex vertical gap={30}>
        <Typography.Title level={2} className="no-margin">
          {isNew ? "Создание отчета" : "Редактирование отчета"}
        </Typography.Title>

        <Spin spinning={pending}>
          <Flex vertical gap={10}>
            <Form
              form={form}
              labelAlign="left"
              labelCol={{ flex: "150px" }}
              initialValues={INITIAL_DATA}
              onFinish={handleSave}
              style={{ maxWidth: 950 }}
            >
              <Form.Item
                label="Название отчета"
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Пожалуйста, введите название отчета",
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Видимость"
                name="visibility"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item label="Фильтры">
                <FiltersList
                  name="filters"
                  definitionsOptions={definitionsOptions}
                />
              </Form.Item>

              <Form.Item label="Результат">
                <TextSectionsList name="text" form={form} />
              </Form.Item>

              {error && (
                <Typography.Text type="danger">{error}</Typography.Text>
              )}
            </Form>

            <Flex gap={10} justify="start">
              <Button key="back" onClick={handleCancel}>
                Отмена
              </Button>
              <Button key="submit" type="primary" onClick={onOk}>
                Сохранить
              </Button>
            </Flex>
          </Flex>
        </Spin>
      </Flex>
    </Flex>
  );
};

export default ReportSettings;
